// src/components/NotificationHandler.js
// Runs silently in the background.
// Saves the FCM token if notification permission is ALREADY granted.
// Does NOT prompt the user — that happens in NotificationSettings.js

import { useEffect } from 'react';
import { getToken, onMessage } from 'firebase/messaging';
import { messaging, db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';

const VAPID_KEY = 'BN-T58CIdOACoVlgKckTQ62D5aUO0YpU7ng5AbnDAezHO4DI5Fz1u7S5g4fkDCw2hi6idxBRrxYuPoXhZMa8IXk';

function NotificationHandler({ user }) {
  useEffect(() => {
    if (!user?.uid) return;

    const saveTokenIfGranted = async () => {
      try {
        if (Notification.permission !== 'granted') {
          console.log('Notification permission not yet granted. User can enable in Settings.');
          return;
        }

        if (!('serviceWorker' in navigator)) {
          console.warn('Service workers are not supported by this browser.');
          return;
        }

        // Wait for the existing active service worker
        const registration = await navigator.serviceWorker.ready;

        const currentToken = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration,
        });

        if (currentToken) {
          console.log('FCM Token acquired for user:', user.uid);
          const userDocRef = doc(db, 'users', user.uid);
          // Use setDoc with merge:true so this works even if the user
          // document doesn't exist yet (updateDoc would silently fail).
          await setDoc(
            userDocRef,
            { fcmTokens: { [currentToken]: true } },
            { merge: true }
          );
          console.log('Token saved for user:', user.uid);
        }
      } catch (error) {
        console.error('Error saving notification token:', error);
      }
    };

    saveTokenIfGranted();

    // Handle messages while the app is in the foreground
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('Foreground message received.', payload);
      if (Notification.permission === 'granted') {
        new Notification(payload.notification?.title || 'HumberTrack', {
          body: payload.notification?.body || 'You have a new notification',
          icon: '/logo192.png',
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [user]);

  return null;
}

export default NotificationHandler;